import { EventTrackingAction, EventTrackingCategory, UserEvent } from './declarations';
import {
  initSentry,
  SentryOptions,
  trackErrorInSentry,
  trackEventInSentry,
  trackUserEventInSentry,
} from './internal/sentry';
import { trackEvent as analyticsTrackEvent } from './internal/analytics';
import { loadGoogleTagManager } from './internal/googleTagManager';
import { loadGoogleAnalytics } from './internal/googleAnalytics';
import { initJourney, trackUserEventInJourney } from './internal/journey';
import { getDeployEnv, DeployEnv } from './internal/deployEnv';
import { loadSegment } from './internal/segment';
import {
  addRUMGlobalContext,
  DatadogOptions,
  getContextualDataFromDatadog,
  initDatadog,
  trackErrorInDatadog,
  trackEventInDatadog,
  trackPerformanceInDatadog,
  trackUserEventInDatadog,
} from './internal/datadog';
import { ContextualData, emptyContextualData, mergeContextualData } from './contextualData';

export interface Observability {
  /**
   * Track events not performed by a user but meaningful internally. For events performed
   * by a user, use `trackUserEvent`.
   *
   * TODO (billy): rename to `trackInternalEvent`
   */
  trackEvent(
    action: EventTrackingAction,
    label?: string,
    contextualData?: Partial<ContextualData>,
    options?: TrackingOptions,
  ): void;
  /**
   * Track events explicitly performed by a user. Specifically, sends event to
   * 1. Journey
   * 2. Datadog RUM
   * 3. Sentry
   * @param event conforms to the strong schema defined in protobuf/journey/data/event.proto
   * @param contextualData contextual data to be merged with parents' contextual datas
   */
  trackUserEvent(event: UserEvent, contextualData?: Partial<ContextualData>): void;
  trackError(
    label: string,
    error?: Error,
    contextualData?: Partial<ContextualData>,
    options?: TrackingOptions,
  ): void;
}

/**
 * Global Observability instance, set by initObservability.
 * You probably want to use observability/react/useObservability instead of this variable directly.
 */
export let globalObservability: Observability | undefined;

export interface ObservabilityOptions {
  env?: DeployEnv;
  debug?: boolean;
  eventTrackingCategory: EventTrackingCategory;
  googleAnalyticsToken: string;
  sentry: SentryOptions;
  datadog: DatadogOptions;
}

export interface TrackingOptions {
  categoryOverride?: EventTrackingCategory;
  addTiming?: boolean;
}

/** Call this before you use useObservability or globalObservability. */
export function initObservability({
  env,
  debug,
  eventTrackingCategory,
  googleAnalyticsToken,
  sentry,
  datadog,
}: ObservabilityOptions): void {
  if (globalObservability) {
    throw new Error('initObservability was already called');
  }

  env = env || getDeployEnv();
  if (env === 'development') {
    if (debug) {
      globalObservability = new DebugObservability();
    } else {
      globalObservability = {
        trackEvent: () => {},
        trackUserEvent: () => {},
        trackError: () => {},
      };
    }
  } else {
    // TODO: Hide these in observability/internal like initSentry.
    loadGoogleAnalytics(googleAnalyticsToken);
    loadGoogleTagManager();
    loadSegment();

    const gitSha = process.env.REACT_APP_GIT_SHA || 'unknown';
    initSentry(env, gitSha, sentry);
    initDatadog(env, gitSha, datadog);
    initJourney();
    globalObservability = new RealObservability(eventTrackingCategory);
  }
}

/** The real thing. It actually calls services like Google Analytics, Segment, Sentry, etc. */
class RealObservability implements Observability {
  constructor(readonly eventTrackingCategory: EventTrackingCategory) {}

  trackEvent(
    action: EventTrackingAction,
    label?: string,
    contextualData: ContextualData = emptyContextualData,
    options?: TrackingOptions,
  ): void {
    // Add contextual data from Datadog (e.g. datadog-rum-session-id).
    contextualData = mergeContextualData(contextualData, getContextualDataFromDatadog());

    analyticsTrackEvent(
      options?.categoryOverride ?? this.eventTrackingCategory,
      action,
      label,
      contextualData.data,
    );
    trackEventInDatadog(action, label, contextualData);
    trackEventInSentry(action, label, contextualData);

    if (options?.addTiming) {
      trackPerformanceInDatadog(`${this.eventTrackingCategory}-${action}-${label}`);
    }
  }

  trackUserEvent(event: UserEvent, contextualData: ContextualData = emptyContextualData): void {
    // TODO (billy)
    // do we need to add contextual data from datadog?
    trackUserEventInJourney(event, contextualData);
    trackUserEventInDatadog(event, contextualData);
    trackUserEventInSentry(event, contextualData);
  }

  trackError(
    label: string,
    error?: Error,
    contextualData: ContextualData = emptyContextualData,
    options?: TrackingOptions,
  ): void {
    // Add contextual data from Datadog (e.g. datadog-rum-session-id).
    contextualData = mergeContextualData(contextualData, getContextualDataFromDatadog());

    // Add contextual data from Sentry (e.g. sentry-event-id).
    const sentryContextualData = trackErrorInSentry(label, error, contextualData);
    contextualData = mergeContextualData(contextualData, sentryContextualData);

    trackErrorInDatadog(label, error, contextualData);
    analyticsTrackEvent(this.eventTrackingCategory, 'error', label, contextualData.data);

    if (options?.addTiming) {
      trackPerformanceInDatadog(`${this.eventTrackingCategory}-error-${label}`);
    }
  }
}

/** For debugging only. Logs events and errors to console. */
class DebugObservability implements Observability {
  trackEvent(
    action: EventTrackingAction,
    label?: string,
    contextualData: ContextualData = emptyContextualData,
  ): void {
    console.info(`trackEvent(${action}, ${label})`, contextualData);
  }

  trackUserEvent(event: UserEvent, contextualData: ContextualData = emptyContextualData): void {
    console.info(`trackUserEvent(${JSON.stringify(event)})`, contextualData);
  }

  trackError(
    label: string,
    error?: Error,
    contextualData: ContextualData = emptyContextualData,
  ): void {
    console.error(`trackError(${label})`, error, contextualData);
  }
}

export const testonly = {
  initDebugObservability: () => {
    globalObservability = new DebugObservability();
  },
  setGlobalObservability: (observability: Observability) => {
    globalObservability = observability;
  },
};

/**
 * This will explicitly add contextual data to the global context for Datadog RUM.
 * It is not intended to include everything passed into trackEvent's contextData
 * which is surfaced in custom RUM events and segment. This should be used for
 * things like experimentation treatments, etc.
 */
export const addDatadogRUMGlobalContext = (context: { [k: string]: unknown }) => {
  addRUMGlobalContext(context);
};
